import type { ComponentChildren, ComponentType } from 'preact';
import type { LucideProps } from 'lucide-preact';
import { Icon } from './Icon';
import { cn } from '@/lib/cn';

export interface SegmentOption<TValue extends string | number | null = string> {
  value: TValue;
  label: ComponentChildren;
  /** Si se pasa, renderiza solo el icono (con label como aria-label/title). */
  icon?: ComponentType<LucideProps>;
  disabled?: boolean;
  /** Override del `aria-label` por opción. Útil cuando el contexto del
   *  grupo no basta (p.ej. "Origen: facebook" en lugar de solo "facebook"). */
  ariaLabel?: string;
}

export interface SegmentedControlProps<TValue extends string | number | null = string> {
  value: TValue;
  options: ReadonlyArray<SegmentOption<TValue>>;
  onChange: (value: TValue) => void;
  size?: 'sm' | 'md';
  /** Para casos como filtros de status donde se necesita `aria-label` general. */
  'aria-label'?: string;
  className?: string;
}

const SIZE = {
  sm: { item: 'h-7 px-2 text-xs', iconSize: 14, iconOnly: 'h-7 w-7 px-0' },
  md: { item: 'h-9 px-3 text-sm', iconSize: 16, iconOnly: 'h-9 w-9 px-0' },
} as const;

/**
 * Segmented control para alternar entre opciones discretas
 * (vista grid/tabla, filtro de status, etc.).
 *
 * Sustituye los grupos ad-hoc de `<div className="rounded-* border bg-surface p-1">`
 * con `<IconButton>` / `<button>` dentro que existían dispersos en
 * `AdsPage`, `CapiAdminSection` y similares.  Mantiene el mismo look
 * pero con API consistente, soporte de teclado completo (arrow keys)
 * y semántica `role="radiogroup"`.
 */
export function SegmentedControl<TValue extends string | number | null = string>({
  value,
  options,
  onChange,
  size = 'sm',
  'aria-label': ariaLabel,
  className,
}: SegmentedControlProps<TValue>) {
  const s = SIZE[size];
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'border-border-subtle bg-surface inline-flex items-center gap-1 rounded-md border p-1',
        className,
      )}
      onKeyDown={(e) => {
        const enabled = options.filter((o) => !o.disabled);
        if (enabled.length === 0) return;
        const idx = enabled.findIndex((o) => o.value === value);
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          const next = enabled[(idx + 1) % enabled.length];
          if (next) onChange(next.value);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = enabled[(idx - 1 + enabled.length) % enabled.length];
          if (prev) onChange(prev.value);
        }
      }}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        // Sin selección, el primer botón habilitado recibe el foco con Tab.
        const tabStop =
          selected ||
          (!options.some((o) => o.value === value) && opt === options.find((o) => !o.disabled));
        const iconOnly = !!opt.icon && typeof opt.label === 'string';
        return (
          <button
            key={String(opt.value ?? '__null__')}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={opt.ariaLabel ?? (iconOnly ? String(opt.label) : undefined)}
            title={iconOnly ? String(opt.label) : undefined}
            disabled={opt.disabled}
            tabIndex={tabStop ? 0 : -1}
            onClick={() => !opt.disabled && onChange(opt.value)}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 rounded font-medium outline-none',
              'duration-med transition-colors',
              'focus-visible:shadow-focus focus-visible:ring-brand focus-visible:ring-1',
              'disabled:cursor-not-allowed disabled:opacity-50',
              iconOnly ? s.iconOnly : s.item,
              selected
                ? 'bg-brand-tint text-brand'
                : 'text-fg-muted hover:bg-surface-hover hover:text-fg-strong',
            )}
          >
            {opt.icon ? <Icon as={opt.icon} size={s.iconSize} /> : null}
            {!iconOnly ? <span>{opt.label}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
